import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check } from 'lucide-react';
import OsScroll from '../ui/OsScroll';
import usePopoverPosition from '../../hooks/usePopoverPosition';
import { cellToText } from '../dbCellText';
import { enumEditorItems, filterEnumItems, NULL_ITEM } from './dbEnumColumns';
import './DbEnumCellEditor.css';

const PAGE = 8;
// Module-level so usePopoverPosition's measure callback stays stable (an
// inline function re-measures on every render, and measuring sets state).
const menuWidth = (r) => Math.max(r.width, 180);

/**
 * The in-cell editor for an ENUM column (owner, 07.10.2026: "ENUM should be
 * shown as a dropdown; clicking the column just enters a text input").
 *
 * A combobox the way DataGrip/TablePlus do it: the cell turns into a filter
 * box with the allowed values listed under it, in the database's order.
 * ↑/↓ (and PgUp/PgDn/Home/End) move, typing filters, Enter or a click commits,
 * Escape cancels, Alt+N stages NULL. A nullable column lists NULL first.
 *
 * It plugs into the grid's existing edit state rather than a parallel one:
 * the highlighted value is mirrored into `editingValue`, and committing calls
 * the same `onCommitEdit` the text editor does — so staging, revert-on-same
 * and Save are exactly the text path's. NULL goes through `onEditNull`, the
 * path the ∅ button uses, because an empty string is a value and not NULL.
 *
 * Leaving the cell (blur) commits what is highlighted, like the text editor
 * commits what is typed. Nothing highlighted (a filter that matched nothing)
 * cancels — a half-typed word is never written into an enum column.
 */
export default function DbEnumCellEditor({
  values,
  nullable,
  value,
  editingValue,
  onEditingValueChange,
  onCommitEdit,
  onCancelEdit,
  onEditNull,
  onEditTab,
}) {
  const currentText = value === null || value === undefined ? null : cellToText(value);
  const items = useMemo(() => enumEditorItems(values, nullable), [values, nullable]);
  // F2 / double-click open the editor with the cell's own text; type-to-edit
  // opens it with the typed character, which is a filter. A Tab flip to/from
  // the record view re-opens it with the value highlighted on the other side —
  // that is an exact value, so it is highlighted again rather than filtered.
  const [filter, setFilter] = useState(() =>
    editingValue && editingValue !== (currentText ?? '') && !items.some((it) => it.value === editingValue)
      ? editingValue
      : ''
  );
  const shown = useMemo(() => filterEnumItems(items, filter), [items, filter]);
  const [cursor, setCursor] = useState(() => {
    if (filter) return filterEnumItems(items, filter).length ? 0 : -1;
    const wanted = editingValue && items.some((it) => it.value === editingValue) ? editingValue : currentText;
    return items.findIndex((it) => (it === NULL_ITEM ? wanted === null : it.value === wanted));
  });
  const highlighted = cursor >= 0 && cursor < shown.length ? shown[cursor] : null;
  const [commitWanted, setCommitWanted] = useState(false);
  const anchorRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const doneRef = useRef(false);
  const listId = useId();

  // Mirror the highlighted value into the grid's edit state, so a commit from
  // anywhere (Enter, click, blur) stages it through the ordinary path.
  useEffect(() => {
    if (highlighted && highlighted !== NULL_ITEM && editingValue !== highlighted.value) {
      onEditingValueChange(highlighted.value);
    }
  }, [highlighted, editingValue, onEditingValueChange]);

  const finish = useCallback(
    (item) => {
      if (doneRef.current) return;
      if (!item) {
        doneRef.current = true;
        onCancelEdit();
      } else if (item === NULL_ITEM) {
        doneRef.current = true;
        if (currentText === null) onCancelEdit();
        else onEditNull?.();
      } else if (editingValue === item.value) {
        // The usual case — the mirror effect already ran. Synchronous on
        // purpose: a blur caused by clicking ANOTHER cell must stage this one
        // before that click moves the grid's edit state on.
        doneRef.current = true;
        onCommitEdit();
      } else {
        onEditingValueChange(item.value);
        setCommitWanted(true);
      }
    },
    [currentText, editingValue, onCancelEdit, onCommitEdit, onEditNull, onEditingValueChange]
  );

  // A commit waits until editingValue carries the highlighted value, so the
  // grid's onCommitEdit (a closure over editingValue) never stages a stale one.
  useEffect(() => {
    if (!commitWanted || doneRef.current) return;
    if (highlighted && highlighted !== NULL_ITEM && editingValue === highlighted.value) {
      doneRef.current = true;
      onCommitEdit();
    }
  }, [commitWanted, highlighted, editingValue, onCommitEdit]);

  useEffect(() => {
    const row = listRef.current?.querySelector(`[data-enum-index="${cursor}"]`);
    row?.scrollIntoView?.({ block: 'nearest' });
  }, [cursor]);

  const pos = usePopoverPosition({
    anchorRef,
    open: true,
    width: menuWidth,
    placement: 'auto',
    gap: 2,
  });

  const move = (delta) => {
    if (shown.length === 0) return;
    setCursor((c) => Math.max(0, Math.min(shown.length - 1, (c < 0 ? (delta > 0 ? -1 : shown.length) : c) + delta)));
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); move(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
    else if (e.key === 'PageDown') { e.preventDefault(); move(PAGE); }
    else if (e.key === 'PageUp') { e.preventDefault(); move(-PAGE); }
    else if (e.key === 'Home' && !filter) { e.preventDefault(); if (shown.length) setCursor(0); }
    else if (e.key === 'End' && !filter) { e.preventDefault(); if (shown.length) setCursor(shown.length - 1); }
    else if (e.key === 'Enter') { e.preventDefault(); finish(highlighted); }
    else if (e.key === 'Escape') { e.preventDefault(); doneRef.current = true; onCancelEdit(); }
    else if (e.key === 'Tab' && onEditTab) {
      e.preventDefault();
      // The flip unmounts this editor and the other view mounts its own. A
      // blur from that teardown must not end the edit: commitEditCell ignores
      // it (flipKeepsEditRef), but NULL (onEditNull) and "no match"
      // (onCancelEdit) would not.
      doneRef.current = true;
      onEditTab();
    }
    else if (e.altKey && e.code === 'KeyN' && onEditNull) {
      e.preventDefault();
      if (nullable) { doneRef.current = true; onEditNull(); }
    }
  };

  const onFilterChange = (e) => {
    const next = e.target.value;
    setFilter(next);
    const list = filterEnumItems(items, next);
    if (!next) {
      setCursor(list.findIndex((it) => (it === NULL_ITEM ? currentText === null : it.value === currentText)));
    } else {
      setCursor(list.length ? 0 : -1);
    }
  };

  const menu =
    typeof document !== 'undefined' && document.body
      ? createPortal(
          <div
            className="auth-picker__menu dbm-enum-menu"
            id={listId}
            role="listbox"
            aria-label="Allowed values"
            // The grid clears its selection on a mousedown outside itself
            // (dbGridClickOutside); picking a value is not leaving the grid.
            data-keeps-selection=""
            style={{
              position: 'fixed',
              ...(pos.dir === 'up' ? { bottom: pos.bottom } : { top: pos.top }),
              left: pos.left,
              minWidth: pos.width,
              zIndex: 10040,
            }}
            // Keep the caret in the filter box: a click must not blur it first.
            // The menu is portaled, but React still bubbles its events to the
            // cell underneath — whose mousedown selects, double-click opens
            // the editor and right-click opens the grid menu. None of that is
            // meant by a click in this list.
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDoubleClick={(e) => e.stopPropagation()}
            onContextMenu={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <OsScroll style={{ maxHeight: Math.min(pos.maxHeight || 280, 280) }}>
              <div className="auth-picker__list auth-picker__list--os" ref={listRef}>
                {shown.length === 0 && <div className="auth-picker__empty">No matching value</div>}
                {shown.map((it, i) => {
                  const isNull = it === NULL_ITEM;
                  const isCurrent = isNull ? currentText === null : it.value === currentText;
                  return (
                    <button
                      key={isNull ? '\u0000null' : `v:${it.value}`}
                      type="button"
                      role="option"
                      tabIndex={-1}
                      data-enum-index={i}
                      aria-selected={i === cursor}
                      className={`auth-picker__row dbm-enum-row${isCurrent ? ' auth-picker__row--active' : ''}${
                        i === cursor ? ' dbm-enum-row--cursor' : ''
                      }${isNull ? ' dbm-enum-row--null' : ''}`}
                      onClick={() => {
                        setCursor(i);
                        finish(it);
                      }}
                    >
                      <span className="min-w-0 flex-1 truncate text-left">{isNull ? 'NULL' : it.value === '' ? '(empty)' : it.value}</span>
                      {isCurrent && <Check size={12} className="auth-picker__icon shrink-0" aria-hidden />}
                    </button>
                  );
                })}
              </div>
            </OsScroll>
          </div>,
          document.body
        )
      : null;

  return (
    <div className="dbm-edit-wrap" ref={anchorRef}>
      <span className="dbm-edit-ghost" aria-hidden>
        {currentText === null ? 'NULL' : currentText}
      </span>
      <input
        ref={inputRef}
        type="text"
        autoFocus
        role="combobox"
        aria-expanded="true"
        aria-controls={listId}
        aria-autocomplete="list"
        className="dbm-pending-input dbm-edit-input dbm-enum-input"
        value={filter}
        placeholder={highlighted ? (highlighted === NULL_ITEM ? 'NULL' : highlighted.value) : currentText ?? 'NULL'}
        // Caret at the end: type-to-edit starts the filter with the typed
        // character, and the next keystrokes must follow it (as in the text editor).
        onFocus={(e) => e.target.setSelectionRange(e.target.value.length, e.target.value.length)}
        onChange={onFilterChange}
        onKeyDown={onKeyDown}
        onBlur={() => finish(highlighted)}
        data-testid="dbm-enum-editor"
      />
      {menu}
    </div>
  );
}
